import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { BookOpen, Brain, FolderOpen, Library, Moon, PenLine, Search, Settings, Sun } from 'lucide-react'
import { burrowAppUrl } from '../lib/env'
import { useTheme } from '../lib/theme'
import { AccountMenu } from './AccountMenu'

// Burrow's own nav bar, a copy of the main app's (src/app/components/AppNav.tsx):
// the same height, logo, items, icons and search box, so Writings sits inside
// Burrow rather than beside it. With Writings as a fifth item it needs more
// room than the app's: labels show from lg and the search box from xl. Writing controls never go here; they live in
// the composer's own tool bar. The app's pages are links out until Writings
// moves into the app; Writings itself is the active item.

const NAV_ITEMS = [
  { path: '/library', label: 'Library', icon: Library },
  { path: '/thinking', label: 'Thinking Space', icon: Brain },
  { path: '/collections', label: 'Collections', icon: FolderOpen },
  { path: '/editions', label: 'Editions', icon: BookOpen },
] as const

const item = 'flex items-center gap-2 rounded-xl px-3.5 py-2 text-[0.88rem] no-underline transition-all duration-200'
const idle = 'text-muted-foreground hover:bg-muted/30 hover:text-foreground'
const iconBtn = 'flex items-center justify-center rounded-xl p-2.5 text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground'

export function BurrowNav({
  sticky = true,
  onSearch,
}: {
  /** Stays at the top as the page scrolls (the app's behaviour). The composer passes false. */
  sticky?: boolean
  /** Where a search goes. Without it, the app's search page. */
  onSearch?: (query: string) => void
}) {
  const { theme, toggleTheme } = useTheme()
  return (
    <header
      className={`${sticky ? 'sticky top-0' : 'relative'} z-40 border-b border-border/40 bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-lg`}
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between font-display md:h-16">
          <a href={burrowAppUrl} className="mr-6 shrink-0 text-[1.3rem] font-medium italic text-primary no-underline">
            Burrow
          </a>

          <nav aria-label="Burrow" className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <a key={path} href={`${burrowAppUrl}${path}`} title={label} className={`${item} ${idle}`}>
                <Icon size={16} aria-hidden />
                <span className="max-lg:sr-only">{label}</span>
              </a>
            ))}
            <Link to="/" aria-current="page" title="Writings" className={`${item} bg-accent/15 text-accent`}>
              <PenLine size={16} aria-hidden />
              <span className="max-lg:sr-only">Writings</span>
            </Link>
          </nav>

          <div className="ml-auto flex min-w-0 items-center gap-1 md:gap-2.5">
            <NavSearch onSearch={onSearch} />
            <Link to="/" aria-label="Writings" className={`${iconBtn} md:hidden`}>
              <PenLine size={18} aria-hidden />
            </Link>
            <button type="button" onClick={toggleTheme} aria-label={theme === 'light' ? 'Dark mode' : 'Light mode'} title={theme === 'light' ? 'Dark mode' : 'Light mode'} className={iconBtn}>
              {theme === 'light' ? <Moon size={17} aria-hidden /> : <Sun size={17} aria-hidden />}
            </button>
            <AccountMenu />
            <a href={`${burrowAppUrl}/settings`} aria-label="Settings" title="Settings" className={`${iconBtn} max-md:hidden`}>
              <Settings size={17} aria-hidden />
            </a>
          </div>
        </div>
      </div>
    </header>
  )
}

/** The app's search box (SearchOverlay's idle and focused looks). */
function NavSearch({ onSearch }: { onSearch?: (query: string) => void }) {
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (onSearch) {
      onSearch(q)
      setQuery('')
      return
    }
    window.location.assign(`${burrowAppUrl}/search${q.length >= 2 ? `?q=${encodeURIComponent(q)}` : ''}`)
  }
  return (
    <form
      role="search"
      onSubmit={submit}
      className={`hidden min-w-0 max-w-xs flex-1 items-center gap-2 rounded-xl border px-3.5 py-2 transition-all duration-200 xl:flex xl:min-w-[12rem] ${
        focused ? 'border-accent/40 bg-card shadow-sm' : 'border-border/40 bg-card/60'
      }`}
    >
      <button type="submit" aria-label="Search" className="shrink-0 text-muted-foreground transition-colors hover:text-accent">
        <Search size={15} aria-hidden />
      </button>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search thoughts..."
        aria-label="Search Burrow"
        className="w-full min-w-0 bg-transparent text-[0.82rem] text-foreground outline-none placeholder:text-muted-foreground/50"
      />
    </form>
  )
}
